import './styles/ReviewActivities.scss';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReviewSpinner from './ReviewSpinner';
import classNames from 'classnames';
import requiresReview from './hocs/requiresReview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReviewType } from '../../../propTypes';
import { apiFetch, formatDateTime, stringParams } from 'utils';
import { compose } from 'redux';
import { duckify, filterDuck } from 'utils/duckify';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { faShare } from '@fortawesome/pro-solid-svg-icons/faShare';
import { flashMessages, reviewActivityItems as reviewActivityItemsDuck } from 'ducks';

/**
 * Displays contact activity history filtered to activity items enabled for the review year.
 *
 * @param review the review object
 * @param reviewActivityItems review activity items
 * @param duckMeta duck loading state
 * @param showFlash function to display flash messages
 * @return {JSX.Element}
 */
const ReviewActivities = ({ review, reviewActivityItems, duckMeta, showFlash }) => {
  const [activities, setActivities] = useState();

  useEffect(() => {
    if (duckMeta.reviewActivityItems.ready) {
      const ids = reviewActivityItems.filter(item => item.enabled).map(item => item.activity_item_id);
      const activitiesUrl = `contacts/${review.contact_id}/activities?` + stringParams({ activity_id: ids });
      apiFetch.get({}, activitiesUrl)
        .then(({ activities }) => setActivities(activities))
        .catch(() => showFlash({ type: 'danger', message: 'An error occurred while loading activities.' }));
    }
  }, [review.contact_id, review.school_year_id, duckMeta.reviewActivityItems.ready]);

  return (
    <div className="review-activities">
      <h2>Additional Activities</h2>

      {!activities && <ReviewSpinner />}

      {activities && <React.Fragment>
        {activities.length === 0 && <div className=".review-activities-none">
          <div className="alert alert-info">
            There are no activities for this applicant.
          </div>
        </div>}

        {activities.length !== 0 && <div className="history-container row">
          {activities.map((activity, i) => {
            const activityType = activity.is_note ? 'note' : 'activity';
            const icon = activity.is_note ? faEdit : faShare;

            return <div
              key={i}
              className={classNames('history-entry', 'container', 'sa-border', activityType)}>
              <div className="row">
                <div className={classNames('col-2', 'bg', activityType)}>
                  <div className={classNames('icon', 'color', activityType)}>
                    <div className={classNames('sa-border', activityType)}>
                      <FontAwesomeIcon icon={icon} />
                      <div className="text">{activityType}</div>
                    </div>
                  </div>
                </div>

                <div className="col-22">
                  <div className="row review-activities-content">
                    <div className="col-17 review-activities-note-text">
                      <strong>{activity.activity_name}</strong>{': '}
                      {activity.note}
                    </div>
                    <div className="col-4">
                      <div className="posted-by">
                        <div className="poster text-end">
                          <em>{activity.author_first_name} {activity.author_last_name}</em>
                        </div>
                      </div>
                    </div>
                    <div className="col-3">
                      {activity.date && <div className="date text-end">
                        {formatDateTime(activity.date, 'fns_date')}
                      </div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>;
          })}
        </div>}
      </React.Fragment>}
    </div>
  );
};

ReviewActivities.propTypes = {
  review: ReviewType.isRequired,
  reviewActivityItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  duckMeta: PropTypes.shape({
    reviewActivityItems: PropTypes.object,
  }).isRequired,
  showFlash: PropTypes.func.isRequired,
};

const mapDucksToProps = ({ review }) => ({
  reviewActivityItems: filterDuck(reviewActivityItemsDuck, { school_year_id: review.school_year_id }),
});

const mapDispatchToProps = (dispatch) => ({
  showFlash: (props) => dispatch(flashMessages.actions.show(props))
});

export default compose(
  requiresReview,
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps)
)(ReviewActivities);
